/**
 * Legacy Sendbird v3 chat selectors.
 *
 * These selectors operate on v3-specific state (threads, connectionStatus, etc.)
 * Shared selectors remain in `@/domain/chat/chatSelector.js`.
 */
import { Map, Set } from 'immutable';
import { CONNECTED } from 'sendbird-utils/lib/constants';
import { selector } from '@/lib/selectors';
import { ghostIdentities, chatParticipantsSelector } from '@/domain/chat/chatSelector';
// eslint-disable-next-line no-restricted-syntax
import { loadedDataCompanyIdSelector } from '@/domain/documents/documentsLeafSelectors';

const env = (state) => state.env;
const chat = (state) => state.chat;
const documents = (state) => state.documents;

// Chat follows the company whose data the store holds, not the one in the URL: it keeps the
// last company on pages that name none, and waits out a company switch until the new
// company's chat users have loaded. A known compromise, to revisit together with the profile.

// Filtering threads with ghost user at the very low level to prevent any further selectors from
// operating ghost channels data/counters etc after ghost was removed from chat participants.
export const chatThreadCommonSelector = selector(chat, env, (c, e) =>
  ghostIdentities.includes(e.userId)
    ? c.threads
    : c.threads.filterNot((thread) => thread.members.some((member) => ghostIdentities.includes(member.userId))),
);

export const chatThreadSelector = selector(
  chatThreadCommonSelector,
  env,
  chatParticipantsSelector,
  (threads, e, users) => {
    if (users.size === 0) return threads;

    // Include current user's ID so threads containing the current user aren't filtered out
    const userEmails = Set(users.map((user) => user.userId)).add(e.userId);

    return threads.filter((thread) => {
      const members = Set(thread.members.map((user) => user.userId));
      return userEmails.intersect(members).equals(members);
    });
  },
);

export const chatUsersSelector = selector(chatParticipantsSelector, env, (u, e) =>
  u.filter((f) => f.userId !== e.userId),
);
export const chatUsersWithoutSuppliers = selector(chatUsersSelector, (users) =>
  users.filter(({ role }) => role !== 'supplier'),
);
export const chatClientsSelector = selector(chatUsersSelector, (list) =>
  list.filter((user) => ['user', 'confidential-user'].includes(user.role)),
);
export const channelsSelector = (state) => state.chat.channels;
export const activeChannelSelector = selector(chat, (c) => c.activeChannel);
export const messageQuerySelector = selector(chat, (c) => c.messageQuery);
export const chatReadyToRenderOnDocumentSelector = selector(chat, (c) => c.readyToRenderOnDocument);

export const chatUnreadThreadSelector = selector(chatThreadSelector, (t) =>
  t.toList().filter((v) => v.unreadMessageCount > 0),
);

// V3 version of documentsUnreadCounterSelector — derives from thread objects
export const documentsUnreadCounterV3Selector = selector(
  chatUnreadThreadSelector,
  loadedDataCompanyIdSelector,
  (t, companyId) =>
    t.reduce((a, v) => (v.companyId === companyId && v.has('documentId') ? a.add(v.documentId) : a), new Set()),
);

export const documentUnreadMessagesCountSelector = selector(
  chatUnreadThreadSelector,
  loadedDataCompanyIdSelector,
  documents,
  (threads, companyId, docs) =>
    threads.reduce(
      (acc, thread) =>
        thread.companyId === companyId && thread.get('documentId') === docs.document.documentID
          ? acc + thread.unreadMessageCount
          : acc,
      0,
    ),
);

export const chatByCompanySelector = selector(chatUnreadThreadSelector, loadedDataCompanyIdSelector, (t, companyId) =>
  t.filter((f) => f.companyId === companyId),
);

export const chatConnectionStatusSelector = selector(chat, (c) => c.connectionStatus);
export const isChatConnectedSelector = selector(chatConnectionStatusSelector, (c) => c === CONNECTED);

export const isOneSignalInitSelector = selector(chat, (c) => c.isOneSignalInit);

export const allowedChatWorkerSelector = selector(chat, (c, i) => c.allowedWorker && !i);

// V3 company unread counter — derives from thread objects grouped by companyId
export const companyUnreadCounterV3Selector = selector(chatUnreadThreadSelector, (t) =>
  t.reduce((acc, thread) => {
    const { companyId } = thread;
    return acc.update(companyId, (count) => (count || 0) + thread.unreadMessageCount);
  }, new Map()),
);

// V3 total unread count — sum of all unread messages across threads
export const totalUnreadCountV3Selector = selector(chatUnreadThreadSelector, (t) =>
  t.reduce((acc, thread) => acc + thread.unreadMessageCount, 0),
);

// V3: context-aware — returns current company's unread count when inside a company,
// falls back to org-wide total when no company is selected (e.g. companies list page)
export const contextUnreadCountV3Selector = selector(
  totalUnreadCountV3Selector,
  companyUnreadCounterV3Selector,
  loadedDataCompanyIdSelector,
  (total, byCompany, companyId) => {
    if (!companyId) return total;
    return byCompany.get(companyId) || 0;
  },
);
